// src/utils/messageProcessing.js

const CHAT_PLATFORMS = ["telegram", "whatsapp", "instagram", "facebook"]; // Add any other chat platforms

/**
 * Determines if a message is from the current user.
 * This needs to be robust based on your data structure from the backend.
 * For Telegram, your backend seems to set `to: "Me"` or `from: "Me"`.
 * For Gmail, `currentUserIdentifier` would be an array of the user's email addresses.
 */
function getMessageSender(message, currentUserIdentifier, activeChannel, influencerNickname) {
    if (!message) return "Unknown";

    const from = message.from;

    if (CHAT_PLATFORMS.includes(activeChannel)) {
        // Standardize "Me" as the sender if it's from the current user
        if (from === "Me" || (currentUserIdentifier === "Me" && from === "Me" )) { // Assuming backend might send "Me"
             return "Me";
        }
        // If 'from' is not "Me", assume it's the influencer/other party
        return from || influencerNickname || "Other"; // Fallback to influencer's nickname or "Other"
    } else { // Gmail or other email
        const userEmails = Array.isArray(currentUserIdentifier) ? currentUserIdentifier : [currentUserIdentifier];
        const fromEmail = (typeof from === 'string' ? from.match(/<([^>]+)>/)?.[1] || from : '').toLowerCase();

        if (userEmails.some(userMail => fromEmail === userMail.toLowerCase())) {
            return "Me";
        }
        return from || "Other"; // The actual sender's email/name
    }
}


export const processAndGroupMessages = (messages, activeChannel, currentUserIdentifier, influencerNickname) => {
    if (!messages || messages.length === 0) {
        return [];
    }

    // Ensure messages are sorted chronologically (oldest to newest)
    const sortedMessages = [...messages].sort((a, b) => new Date(a.date) - new Date(b.date));

    if (!CHAT_PLATFORMS.includes(activeChannel)) {
        // For non-chat platforms (e.g., Gmail)
        return sortedMessages.map((msg, index) => ({
            ...msg,
            id: msg.id || `email_${index}_${new Date(msg.date).getTime()}`, // Ensure a unique ID
            displayType: 'email',
            senderIdentity: getMessageSender(msg, currentUserIdentifier, activeChannel, influencerNickname) // "Me" or actual sender
        }));
    }

    // Logic for chat platforms: Group consecutive messages from the same sender
    const groupedChatMessages = [];
    let currentGroup = null;

    for (const message of sortedMessages) {
        // Determine sender based on 'from' field relative to the current user or influencer
        // This part is crucial and depends on how your backend structures 'from' and 'to' for chat.
        // Let's assume `message.from` holds the sender's identifier (e.g., influencer's name or "Me")
        const senderName = getMessageSender(message, currentUserIdentifier, activeChannel, influencerNickname);

        if (currentGroup && currentGroup.sender === senderName) {
            // Same sender, add to current group
            currentGroup.messages.push({
                body: message.body,
                originalDate: message.date,
                id: message.id || `msg_${currentGroup.messages.length}_${new Date(message.date).getTime()}`,
            });
            currentGroup.endDate = message.date; // Update end time of the group
        } else {
            // Different sender or first message, start a new group
            if (currentGroup) {
                groupedChatMessages.push(currentGroup);
            }
            currentGroup = {
                displayType: 'chat_group',
                id: message.id || `group_${new Date(message.date).getTime()}`,
                sender: senderName, // "Me" or Influencer's Name
                date: message.date, // Timestamp of the first message in the group
                endDate: message.date,
                messages: [{
                    body: message.body,
                    originalDate: message.date,
                    id: message.id || `msg_0_${new Date(message.date).getTime()}`,
                }],
                // Store other relevant top-level fields from the first message if needed
                // e.g., if chats can have a 'subject' equivalent or thread ID
                originalFrom: message.from, // Keep original fields for reference
                originalTo: message.to,
            };
        }
    }

    if (currentGroup) { // Add the last group
        groupedChatMessages.push(currentGroup);
    }

    return groupedChatMessages;
};